import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { companyApi, savedTeamsApi } from '../api/endpoints.ts';
import { useSession } from '../state/session.tsx';
import { companyHasPlanFeature } from './plan-gate.tsx';
import { Alert, Button, Checkbox, Select, TextInput } from './ui.tsx';
import { AssigneePicker } from './schedule/assignee-picker.tsx';
import type { SavedTeam } from '@shared/types/index.ts';

const empty = { name: '', color: '#2563eb', memberIds: [] as string[], leadUserId: null as string | null };
export default function SavedTeamsSection() {
  const { t } = useTranslation(),
    { company } = useSession(),
    cache = useQueryClient();
  const enabled = companyHasPlanFeature(company, 'scheduling');
  const teams = useQuery({ queryKey: ['saved-teams'], queryFn: () => savedTeamsApi.list(), enabled });
  const people = useQuery({ queryKey: ['team'], queryFn: () => companyApi.users(), enabled });
  const [editing, setEditing] = useState<SavedTeam | null>(null);
  const [draft, setDraft] = useState(empty);
  const [open, setOpen] = useState(false),
    [future, setFuture] = useState(false);
  const [error, setError] = useState<string | null>(null),
    [summary, setSummary] = useState<string | null>(null);
  const refresh = () => {
    cache.invalidateQueries({ queryKey: ['saved-teams'] });
    cache.invalidateQueries({ queryKey: ['series'] });
    cache.invalidateQueries({ queryKey: ['appointments'] });
  };
  const save = useMutation({
    mutationFn: async () =>
      editing
        ? savedTeamsApi.update(editing.id, { ...draft, expectedRevision: editing.revision, updateFutureVisits: future })
        : savedTeamsApi.create(draft),
    onSuccess: (result) => {
      refresh();
      setOpen(false);
      setError(null);
      if ('updatedVisits' in result)
        setSummary(t('savedTeams.updated', { count: result.updatedVisits, preserved: result.preservedVisits }));
    },
    onError: () => setError(t('savedTeams.saveError')),
  });
  const archive = useMutation({
    mutationFn: (entry: SavedTeam) =>
      savedTeamsApi.update(entry.id, { expectedRevision: entry.revision, isActive: false }),
    onSuccess: refresh,
    onError: () => setError(t('savedTeams.saveError')),
  });
  if (!enabled) return null;
  return (
    <section className="card mt-4" aria-labelledby="saved-teams-heading">
      <h3 id="saved-teams-heading" className="text-lg font-semibold">
        {t('savedTeams.title')}
      </h3>
      <p className="mb-4 text-sm text-[color:var(--color-muted)]">{t('savedTeams.description')}</p>
      {error ? <Alert tone="error">{error}</Alert> : null}
      {summary ? <p role="status">{summary}</p> : null}
      {teams.isLoading ? <p role="status">{t('savedTeams.loading')}</p> : null}
      {teams.isError ? (
        <Alert tone="error">
          <p>{t('savedTeams.loadError')}</p>
          <Button className="mt-3" variant="secondary" busy={teams.isFetching} onClick={() => void teams.refetch()}>
            {t('common.retry')}
          </Button>
        </Alert>
      ) : null}
      {teams.isSuccess && teams.data.items.length === 0 ? (
        <p className="mb-4 text-sm text-[color:var(--color-muted)]">{t('savedTeams.empty')}</p>
      ) : null}
      <div className="mb-4 space-y-3">
        {(teams.data?.items ?? []).map((entry) => (
          <div className="flex flex-wrap items-center gap-3" key={entry.id}>
            {/* The same color the editor picks and the calendar paints this
                team's visits with, so the list says which team is which. */}
            <span
              aria-hidden="true"
              className="inline-block h-4 w-4 shrink-0 rounded-full border border-[color:var(--color-line)]"
              style={{ backgroundColor: entry.color }}
            />
            <span className="min-w-0 break-words font-medium">
              {t('savedTeams.chip', { name: entry.name, count: entry.memberIds.length })}
              {entry.isActive ? '' : ` (${t('savedTeams.archived')})`}
            </span>
            {entry.isActive ? (
              <>
                <Button
                  variant="secondary"
                  onClick={() => {
                    setEditing(entry);
                    setDraft({
                      name: entry.name,
                      color: entry.color,
                      memberIds: entry.memberIds,
                      leadUserId: entry.leadUserId,
                    });
                    setFuture(false);
                    setOpen(true);
                    setError(null);
                  }}
                >
                  {t('savedTeams.edit')}
                </Button>
                <Button variant="secondary" busy={archive.isPending} onClick={() => archive.mutate(entry)}>
                  {t('savedTeams.archive')}
                </Button>
              </>
            ) : null}
          </div>
        ))}
      </div>
      {open ? (
        <form
          onSubmit={(event) => {
            event.preventDefault();
            save.mutate();
          }}
        >
          <TextInput
            label={t('savedTeams.name')}
            value={draft.name}
            maxLength={160}
            required
            onChange={(event) => setDraft({ ...draft, name: event.target.value })}
          />
          <TextInput
            label={t('savedTeams.color')}
            type="color"
            value={draft.color}
            onChange={(event) => setDraft({ ...draft, color: event.target.value })}
          />
          <AssigneePicker
            value={draft.memberIds}
            onChange={(memberIds) =>
              setDraft({
                ...draft,
                memberIds,
                leadUserId: draft.leadUserId && memberIds.includes(draft.leadUserId) ? draft.leadUserId : null,
              })
            }
            disabled={save.isPending}
          />
          <Select
            label={t('savedTeams.lead')}
            value={draft.leadUserId ?? ''}
            onChange={(event) => setDraft({ ...draft, leadUserId: event.target.value || null })}
            options={[
              { value: '', label: t('savedTeams.noLead') },
              ...draft.memberIds.map((id) => ({
                value: id,
                label: people.data?.items.find((member) => member.id === id)?.fullName ?? id,
              })),
            ]}
          />
          {editing ? <Alert tone="info">{t('employeePicker.impact')}</Alert> : null}
          {editing ? (
            <Checkbox
              label={t('savedTeams.updateFuture')}
              hint={t('savedTeams.updateFutureHint')}
              checked={future}
              onChange={(event) => setFuture(event.target.checked)}
            />
          ) : null}
          <div className="flex gap-3">
            <Button type="submit" busy={save.isPending} disabled={!draft.name.trim() || draft.memberIds.length === 0}>
              {t('savedTeams.save')}
            </Button>
            <Button variant="secondary" disabled={save.isPending} onClick={() => setOpen(false)}>
              {t('savedTeams.cancel')}
            </Button>
          </div>
        </form>
      ) : (
        <Button
          onClick={() => {
            setEditing(null);
            setDraft(empty);
            setOpen(true);
            setError(null);
          }}
        >
          {t('savedTeams.create')}
        </Button>
      )}
    </section>
  );
}
